import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import { EmptyState } from '@/components/ui';
import PageSkeleton from '@/components/PageSkeleton';
import { ActivityHistoryDocument, ShareBookActionType } from '@/types/activityHistory';
import ActivityHistoryItem from './ActivityHistoryItem';
import RestoreConfirmDialog from './RestoreConfirmDialog';

interface ActivityHistoryListProps {
  histories: ActivityHistoryDocument[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  isHost?: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  onRestore: (history: ActivityHistoryDocument) => Promise<void>;
  onFilter?: (filter: any) => void;
  onClearFilter?: () => void;
}

const ActivityHistoryList: React.FC<ActivityHistoryListProps> = ({
  histories,
  loading,
  loadingMore,
  hasMore,
  error,
  isHost = false,
  onLoadMore,
  onRefresh,
  onRestore,
  onFilter,
  onClearFilter
}) => {
  const [restoreDialog, setRestoreDialog] = useState<{
    open: boolean;
    history: ActivityHistoryDocument | null;
    loading: boolean;
  }>({
    open: false,
    history: null,
    loading: false
  });

  const handleRestore = (history: ActivityHistoryDocument) => {
    setRestoreDialog({
      open: true,
      history,
      loading: false
    });
  };

  const handleConfirmRestore = async () => {
    if (!restoreDialog.history) return;

    setRestoreDialog(prev => ({ ...prev, loading: true }));

    try {
      await onRestore(restoreDialog.history);
      setRestoreDialog({
        open: false,
        history: null,
        loading: false
      });
    } catch (error) {
      setRestoreDialog(prev => ({ ...prev, loading: false }));
      console.error('복원 실패:', error);
    }
  };

  const handleCancelRestore = () => {
    setRestoreDialog({
      open: false,
      history: null,
      loading: false
    });
  };

  if (loading) {
    return <PageSkeleton blocks={[72, 72, 72, 72]} />;
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          borderRadius: 2,
          '& .MuiAlert-message': {
            fontSize: '1rem'
          }
        }}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={onRefresh}
          >
            다시 시도
          </Button>
        }
      >
        활동 기록을 불러올 수 없습니다: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* 활동 기록 목록 */}
      {histories.length === 0 ? (
        <Paper sx={{ background: 'var(--bg-elev-1)', border: '1px solid var(--border-subtle)' }}>
          <EmptyState
            title="활동 기록이 없습니다"
            description="아직 공유가계부에서 활동이 발생하지 않았습니다. 지출 추가, 수정, 삭제 등의 작업을 하면 여기에 기록됩니다."
          />
        </Paper>
      ) : (
        <Box>
          {histories.map((history) => (
            <ActivityHistoryItem
              key={history.id}
              history={history}
              isHost={isHost}
              onRestore={handleRestore}
            />
          ))}

          {/* 더 보기 버튼 */}
          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                onClick={onLoadMore}
                disabled={loadingMore}
                variant="outlined"
                sx={{ borderRadius: 2, px: 4 }}
              >
                {loadingMore ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    로딩 중...
                  </>
                ) : (
                  '더 보기'
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* 복원 확인 다이얼로그 */}
      <RestoreConfirmDialog
        open={restoreDialog.open}
        history={restoreDialog.history}
        onConfirm={handleConfirmRestore}
        onCancel={handleCancelRestore}
        loading={restoreDialog.loading}
      />
    </Box>
  );
};

export default ActivityHistoryList;
